import { stripHtml, textToHtml } from '../compose/composeHelpers';

export const IMPROVE_LANGUAGES = [
  'English', 'Kannada', 'Hindi', 'Malayalam', 'Tamil',
  'Arabic', 'French', 'German', 'Japanese', 'Spanish',
];

export const IMPROVE_OPTIONS = [
  {
    id: 'improve',
    icon: '✍',
    label: 'Improve Writing',
    description: 'Better readability, sentence flow, and wording',
    bullets: ['Improve readability', 'Better sentence flow', 'Better wording'],
  },
  {
    id: 'grammar',
    icon: '✔',
    label: 'Fix Grammar',
    description: 'Grammar, spelling, punctuation, capitalization',
    bullets: ['Grammar', 'Spelling', 'Punctuation', 'Capitalization'],
  },
  {
    id: 'rewrite',
    icon: '✏',
    label: 'Rewrite',
    description: 'Rewrite naturally, keep meaning',
    bullets: ['Rewrite naturally', 'Keep meaning'],
  },
  {
    id: 'professional',
    icon: '💼',
    label: 'Professional',
    description: 'Business tone',
    bullets: ['Business tone'],
  },
  {
    id: 'friendly',
    icon: '😊',
    label: 'Friendly',
    description: 'Warm tone',
    bullets: ['Warm tone'],
  },
  {
    id: 'formal',
    icon: '🏛',
    label: 'Formal',
    description: 'Formal tone',
    bullets: ['Formal tone'],
  },
  {
    id: 'shorter',
    icon: '✂',
    label: 'Shorter',
    description: 'Reduce unnecessary words',
    bullets: ['Reduce unnecessary words'],
  },
  {
    id: 'longer',
    icon: '📖',
    label: 'Longer',
    description: 'Expand professionally',
    bullets: ['Expand professionally'],
  },
  {
    id: 'translate',
    icon: '🌍',
    label: 'Translate',
    description: 'Auto-detect language, then translate',
    bullets: ['Auto detect language'],
    hasLanguages: true,
  },
  {
    id: 'continue',
    icon: '➡',
    label: 'Continue Writing',
    description: 'Generate the next paragraphs from the cursor',
    bullets: ['Continue from the cursor', 'Generate the next paragraphs'],
  },
  {
    id: 'summarize',
    icon: '📄',
    label: 'Summarize',
    description: 'Create a concise version',
    bullets: ['Create concise version'],
  },
];

/** Map UI action → stream task endpoint. */
export function actionToStreamTask(action) {
  if (action === 'continue') return 'continue';
  if (action === 'grammar') return 'grammar';
  if (action === 'translate') return 'translate';
  if (action === 'rewrite' || action === 'professional' || action === 'friendly' || action === 'formal') {
    return 'rewrite';
  }
  return 'improve';
}

export function buildImprovePayload(action, {
  text,
  selectionOnly,
  targetLanguage,
  beforeCursor,
  afterCursor,
} = {}) {
  const styleMap = {
    professional: 'professional',
    friendly: 'friendly',
    formal: 'formal',
    rewrite: 'professional',
  };

  return {
    action,
    text,
    selectionOnly: Boolean(selectionOnly),
    targetLanguage: targetLanguage || 'English',
    language: targetLanguage || 'English',
    style: styleMap[action],
    beforeCursor: beforeCursor || '',
    afterCursor: afterCursor || '',
  };
}

export function editorHasContent(editorEl, bodyHtml) {
  // Prefer live DOM so paste/drop shows the button before React state catches up.
  if (editorEl) {
    const live = stripHtml(editorEl.innerHTML);
    if (live) return true;
    // Some pastes leave text in textContent even when innerHTML looks empty-ish.
    if (String(editorEl.textContent || '').trim()) return true;
  }
  if (bodyHtml != null) return Boolean(stripHtml(bodyHtml));
  return false;
}

/**
 * Anchor layer is only as tall as the visible compose body (overflow clips).
 * Never add scrollTop — clamp into the visible client box so paste always shows.
 */
export function clampToVisibleHost(top, left, host, btnWidth = 168, btnHeight = 36) {
  if (!host) {
    return { top: Math.max(top, 8), left: Math.max(left, 8) };
  }
  const maxTop = Math.max(host.clientHeight - btnHeight - 8, 8);
  const maxLeft = Math.max(host.clientWidth - btnWidth - 8, 8);
  return {
    top: Math.min(Math.max(top, 8), maxTop),
    left: Math.min(Math.max(left, 8), maxLeft),
  };
}

/** Sticky fallback in the visible bottom-right of the compose body (paste-safe). */
export function getVisibleFallbackMetrics(editorEl) {
  if (!editorEl || !editorHasContent(editorEl)) return null;
  const host = editorEl.closest('.compose-body-section') || editorEl;
  const fullText = stripHtml(editorEl.innerHTML) || String(editorEl.textContent || '').trim();
  const pos = clampToVisibleHost(
    host.clientHeight - 44,
    host.clientWidth - 176,
    host
  );
  return {
    hasSelection: false,
    selectedText: '',
    range: null,
    top: pos.top,
    left: pos.left,
    beforeCursor: fullText,
    afterCursor: '',
    fullText,
  };
}

/** Fallback near content end, clamped into the visible viewport. */
export function getContentEndMetrics(editorEl) {
  if (!editorEl || !editorHasContent(editorEl)) return null;
  const host = editorEl.closest('.compose-body-section') || editorEl;
  const hostRect = host.getBoundingClientRect();
  const fullText = stripHtml(editorEl.innerHTML) || String(editorEl.textContent || '').trim();

  try {
    const range = document.createRange();
    range.selectNodeContents(editorEl);
    range.collapse(false);
    const rects = range.getClientRects();
    const rect = rects.length
      ? rects[rects.length - 1]
      : editorEl.getBoundingClientRect();
    // Viewport-relative (no scrollTop) so the absolute layer can show the button.
    const rawTop = rect.bottom - hostRect.top + 8;
    const rawLeft = rect.left - hostRect.left + 8;
    const pos = clampToVisibleHost(rawTop, rawLeft, host);
    return {
      hasSelection: false,
      selectedText: '',
      range,
      rect,
      hostRect,
      top: pos.top,
      left: pos.left,
      beforeCursor: fullText,
      afterCursor: '',
      fullText,
    };
  } catch (_) {
    return getVisibleFallbackMetrics(editorEl);
  }
}

/**
 * Get caret / selection metrics relative to the VISIBLE compose body box.
 */
export function getCaretMetrics(editorEl) {
  if (!editorEl) return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  if (!editorEl.contains(range.commonAncestorContainer)) return null;

  const selectedText = String(sel.toString() || '');
  const hasSelection = selectedText.trim().length > 0;

  const clientRects = range.getClientRects();
  let rect = clientRects.length
    ? clientRects[clientRects.length - 1]
    : range.getBoundingClientRect();
  if (!rect || (rect.width === 0 && rect.height === 0)) {
    const node = range.startContainer;
    const el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    rect = el?.getBoundingClientRect?.() || { top: 0, left: 0, bottom: 24, right: 0, width: 0, height: 24 };
  }

  const host = editorEl.closest('.compose-body-section') || editorEl;
  const hostRect = host.getBoundingClientRect();
  const plainBefore = getTextBeforeCaret(editorEl, range);
  const plainAfter = getTextAfterCaret(editorEl, range);
  // Visible-box coordinates only — scrollTop would push the button below the clip edge.
  const pos = clampToVisibleHost(
    rect.bottom - hostRect.top + 8,
    rect.left - hostRect.left + 8,
    host,
    hasSelection ? 160 : 168
  );

  return {
    hasSelection,
    selectedText: selectedText.trim(),
    range: range.cloneRange(),
    rect,
    hostRect,
    top: pos.top,
    left: pos.left,
    beforeCursor: plainBefore,
    afterCursor: plainAfter,
    fullText: stripHtml(editorEl.innerHTML) || String(editorEl.textContent || '').trim(),
  };
}

function getTextBeforeCaret(editorEl, range) {
  try {
    const pre = range.cloneRange();
    pre.selectNodeContents(editorEl);
    pre.setEnd(range.startContainer, range.startOffset);
    return pre.toString();
  } catch (_) {
    return '';
  }
}

function getTextAfterCaret(editorEl, range) {
  try {
    const post = range.cloneRange();
    post.selectNodeContents(editorEl);
    post.setStart(range.endContainer, range.endOffset);
    return post.toString();
  } catch (_) {
    return '';
  }
}

export function applyHtmlToEditor(editorEl, html, mode = 'replace') {
  if (!editorEl) return editorEl?.innerHTML || '';
  if (mode === 'append') {
    const next = `${editorEl.innerHTML || ''}${editorEl.innerHTML ? '<br/><br/>' : ''}${html}`;
    editorEl.innerHTML = next;
    return next;
  }
  editorEl.innerHTML = html;
  return html;
}

export function replaceRangeWithHtml(range, html) {
  if (!range) return;
  range.deleteContents();
  const temp = document.createElement('div');
  temp.innerHTML = html || '';
  const frag = document.createDocumentFragment();
  let last = null;
  while (temp.firstChild) {
    last = temp.firstChild;
    frag.appendChild(last);
  }
  range.insertNode(frag);
  if (last) {
    const sel = window.getSelection();
    const after = document.createRange();
    after.setStartAfter(last);
    after.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(after);
  }
}

export function toPreviewHtml(text) {
  return textToHtml(String(text || '').trim());
}

export { stripHtml, textToHtml };
